// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the iOS/Android shell (konvo_mobile, Capacitor) loads this web app and
// injects `window.Capacitor` before any page script runs. Plugins are reached
// through that bridge so the web bundle carries no Capacitor dependency.
// Shapes mirror konvo_mobile/NATIVE_CONTRACT.md; change that file first.

export type NativeMobilePlatform = 'ios' | 'android';

export interface NativePluginListenerHandle {
	remove(): Promise<void>;
}

interface CapacitorGlobal {
	isNativePlatform?: () => boolean;
	getPlatform?: () => string;
	isPluginAvailable?: (name: string) => boolean;
	registerPlugin?: <T>(name: string) => T;
	Plugins?: Record<string, unknown>;
}

const NATIVE_UA_PATTERN = /KonvoMobile\/\S+ \((ios|android)\)/;

function capacitor(): CapacitorGlobal | null {
	const value = (window as {Capacitor?: CapacitorGlobal}).Capacitor;
	return value ?? null;
}

export function isNativeMobileShell(): boolean {
	const cap = capacitor();
	if (cap?.isNativePlatform?.()) return true;
	return NATIVE_UA_PATTERN.test(navigator.userAgent);
}

export function getNativeMobilePlatform(): NativeMobilePlatform | null {
	if (!isNativeMobileShell()) return null;
	const fromBridge = capacitor()?.getPlatform?.();
	if (fromBridge === 'ios' || fromBridge === 'android') return fromBridge;
	const match = NATIVE_UA_PATTERN.exec(navigator.userAgent);
	return (match?.[1] as NativeMobilePlatform | undefined) ?? null;
}

const pluginCache = new Map<string, unknown>();

type RawPlugin = Record<string | symbol, unknown> & {
	addListener?: (eventName: string, listener: (event: unknown) => void) => unknown;
};

// The injected bridge (no @capacitor/core on the page) exposes plugins as
// `Capacitor.Plugins.<name>`, whose addListener returns the handle directly
// rather than a promise. Normalise it so callers can always `await` it.
function adaptPlugin<T extends object>(raw: RawPlugin): T {
	return new Proxy(raw, {
		get(target, prop) {
			if (prop === 'addListener') {
				return (eventName: string, listener: (event: unknown) => void) =>
					Promise.resolve(target.addListener?.(eventName, listener) as NativePluginListenerHandle);
			}
			const value = target[prop];
			return typeof value === 'function' ? value.bind(target) : value;
		},
	}) as unknown as T;
}

export function getNativeMobilePlugin<T extends object>(name: string): T | null {
	if (!isNativeMobileShell()) return null;
	const cached = pluginCache.get(name);
	if (cached) return cached as T;
	const cap = capacitor();
	if (!cap) return null;
	if (cap.isPluginAvailable && !cap.isPluginAvailable(name)) return null;
	const raw = (cap.Plugins?.[name] as RawPlugin | undefined) ?? (cap.registerPlugin?.<RawPlugin>(name) as RawPlugin);
	if (!raw) return null;
	const plugin = adaptPlugin<T>(raw);
	pluginCache.set(name, plugin);
	return plugin;
}

// ---- Plugin shapes ----

export interface NativeSession {
	token: string | null;
	userId: string | null;
	apiBase: string;
	gatewayUrl: string;
	apiVersion: number;
	mediaBase?: string | null;
}

export interface NativeAppInfo {
	platform: NativeMobilePlatform;
	appVersion: string;
	build: string;
	osVersion: string;
	deviceModel: string;
	pushEnvironment: 'production' | 'development';
}

export interface KonvoNativePlugin {
	/** iOS builds with an in-app system authentication sheet. */
	authenticate?(options: {url: string}): Promise<{url: string}>;
	getInfo(): Promise<NativeAppInfo>;
	/** Additive lifecycle API; absent on older shells. */
	getAppState?(): Promise<{isActive: boolean}>;
	getCachedMessages?(options: {
		userId: string;
		apiBase: string;
	}): Promise<{pages: Array<import('@app/features/messaging/cache/ChatCache').CachedChatPage>}>;
	setSession(session: NativeSession): Promise<void>;
	setBadge(options: {count: number}): Promise<void>;
	setActiveChannel(options: {channelId: string | null}): Promise<void>;
	setAppearance(options: {dark: boolean; background: string}): Promise<void>;
	openSettings(): Promise<void>;
	getLaunchUrl(): Promise<{url: string | null}>;
	/** Widgets builds only (NATIVE_CONTRACT.md "Widgets"); older shells lack it. */
	reloadWidgets?(): Promise<void>;
	addListener(eventName: 'deepLink', listener: (event: {url: string}) => void): Promise<NativePluginListenerHandle>;
	addListener(
		eventName: 'appStateChange',
		listener: (event: {isActive: boolean}) => void,
	): Promise<NativePluginListenerHandle>;
	addListener(
		eventName: 'resume',
		listener: (event: Record<string, never>) => void,
	): Promise<NativePluginListenerHandle>;
}

export type NativePermissionState = 'granted' | 'denied' | 'prompt' | 'provisional';

export interface KonvoPushPlugin {
	getPermission(): Promise<{state: NativePermissionState}>;
	requestPermission(): Promise<{state: NativePermissionState}>;
	register(): Promise<{registered: boolean; platforms: Array<string>}>;
	unregister(): Promise<void>;
	addListener(
		eventName: 'notificationTap',
		listener: (event: {url: string; targetUserId: string | null; data: Record<string, string>}) => void,
	): Promise<NativePluginListenerHandle>;
}

export type NativeCallPhase = 'idle' | 'incoming' | 'outgoing' | 'connecting' | 'connected' | 'reconnecting' | 'ended';

export type NativeCallEndedReason =
	| 'local'
	| 'remote'
	| 'declined'
	| 'answeredElsewhere'
	| 'declinedElsewhere'
	| 'missed'
	| 'failed';

export type NativeAudioRoute = 'earpiece' | 'speaker' | 'bluetooth' | 'headphones' | 'carplay' | 'unknown';

export interface NativeScreenShare {
	connectionId: string;
	userId: string;
}

export interface NativeCallState {
	phase: NativeCallPhase;
	channelId: string | null;
	guildId: string | null;
	connectionId: string | null;
	title: string | null;
	muted: boolean;
	deafened: boolean;
	speaker: boolean;
	audioRoute: NativeAudioRoute;
	startedAt: number | null;
	endedReason: NativeCallEndedReason | null;
	error: string | null;
	screenShareViewingSupported?: boolean;
	screenShares?: Array<NativeScreenShare>;
	/** Absent on older shells: they only have the full-screen viewer. */
	screenSharePreviewSupported?: boolean;
	pictureInPictureSupported?: boolean;
}

/** Names and avatars for the native screen-share surfaces (native only knows ids). */
export interface NativeScreenShareInfo {
	sharerName: string;
	sharerAvatarUrl: string | null;
	callTitle: string | null;
	peopleCount: number;
	peopleAvatarUrls: Array<string>;
}

export interface NativeScreenSharePreview {
	channelId: string;
	connectionId: string;
	info: NativeScreenShareInfo;
	/** CSS px in the web view's viewport. */
	rect: {x: number; y: number; width: number; height: number};
	radius: number;
	visible: boolean;
}

export interface KonvoCallsPlugin {
	isSupported(): Promise<{supported: boolean}>;
	getState(): Promise<NativeCallState>;
	startCall(options: {
		channelId: string;
		guildId: string | null;
		title: string;
		avatarUrl?: string | null;
		selfMute?: boolean;
	}): Promise<void>;
	reportIncomingCall(options: {
		channelId: string;
		messageId: string | null;
		callerId: string;
		title: string;
		avatarUrl?: string | null;
		guildId?: null;
	}): Promise<void>;
	answerCall(options: {channelId: string}): Promise<void>;
	declineCall(options: {channelId: string}): Promise<void>;
	cancelIncomingCall(options: {
		channelId: string;
		reason: 'answeredElsewhere' | 'declinedElsewhere' | 'remote' | 'missed';
	}): Promise<void>;
	endCall(): Promise<void>;
	setMuted(options: {muted: boolean}): Promise<void>;
	setDeafened(options: {deafened: boolean}): Promise<void>;
	setSpeaker(options: {on: boolean}): Promise<void>;
	watchScreenShare(options: {channelId: string; connectionId: string; info?: NativeScreenShareInfo}): Promise<void>;
	showScreenSharePreview(options: NativeScreenSharePreview): Promise<void>;
	hideScreenSharePreview(): Promise<void>;
	addListener(eventName: 'state', listener: (event: NativeCallState) => void): Promise<NativePluginListenerHandle>;
	addListener(
		eventName: 'speaking',
		listener: (event: {userIds: Array<string>}) => void,
	): Promise<NativePluginListenerHandle>;
}

export interface NativeSharedItem {
	kind: 'file' | 'image' | 'video' | 'url' | 'text';
	name: string | null;
	mimeType: string | null;
	size: number | null;
	text: string | null;
}

export interface NativePendingShare {
	id: string;
	createdAt: number;
	items: Array<NativeSharedItem>;
}

export interface KonvoSharePlugin {
	getPendingShare(): Promise<{share: NativePendingShare | null}>;
	readChunk(options: {
		shareId: string;
		index: number;
		offset: number;
		length: number;
	}): Promise<{base64: string; eof: boolean}>;
	clearShare(options: {shareId: string}): Promise<void>;
	addListener(eventName: 'shareReceived', listener: (event: {id: string}) => void): Promise<NativePluginListenerHandle>;
}

export type NativeActivityKind = 'timer' | 'meeting';

export interface NativeActivityContent {
	title: string;
	subtitle: string | null;
	status: 'running' | 'upcoming' | 'live' | 'ended';
	startedAt: number | null;
	startsAt: number | null;
	endsAt: number | null;
	url: string | null;
	actionUrl: string | null;
}

export interface KonvoActivitiesPlugin {
	isSupported(): Promise<{supported: boolean}>;
	start(options: {kind: NativeActivityKind; id: string; content: NativeActivityContent}): Promise<void>;
	update(options: {kind: NativeActivityKind; id: string; content: NativeActivityContent}): Promise<void>;
	end(options: {kind: NativeActivityKind; id: string; dismissNow?: boolean}): Promise<void>;
	endAll(options?: {kind?: NativeActivityKind}): Promise<void>;
	addListener(
		eventName: 'action',
		// 'agents': the server-driven agents Live Activity (NATIVE_CONTRACT.md); the web never starts one.
		listener: (event: {kind: NativeActivityKind | 'agents'; id: string; action: 'stop' | 'join' | 'open'}) => void,
	): Promise<NativePluginListenerHandle>;
}

export interface CapacitorKeyboardPlugin {
	setAccessoryBarVisible(options: {isVisible: boolean}): Promise<void>;
	hide(): Promise<void>;
}

export interface CapacitorSplashScreenPlugin {
	hide(options?: {fadeOutDuration?: number}): Promise<void>;
}

export interface CapacitorHapticsPlugin {
	impact(options: {style: 'HEAVY' | 'MEDIUM' | 'LIGHT'}): Promise<void>;
	notification(options: {type: 'SUCCESS' | 'WARNING' | 'ERROR'}): Promise<void>;
	selectionChanged(): Promise<void>;
}

export const NativePlugins = {
	native: () => getNativeMobilePlugin<KonvoNativePlugin>('KonvoNative'),
	push: () => getNativeMobilePlugin<KonvoPushPlugin>('KonvoPush'),
	calls: () => getNativeMobilePlugin<KonvoCallsPlugin>('KonvoCalls'),
	share: () => getNativeMobilePlugin<KonvoSharePlugin>('KonvoShare'),
	activities: () => getNativeMobilePlugin<KonvoActivitiesPlugin>('KonvoActivities'),
	keyboard: () => getNativeMobilePlugin<CapacitorKeyboardPlugin>('Keyboard'),
	splash: () => getNativeMobilePlugin<CapacitorSplashScreenPlugin>('SplashScreen'),
	haptics: () => getNativeMobilePlugin<CapacitorHapticsPlugin>('Haptics'),
};
